import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { sanitizeUserName, UserService } from "../utils/users";
import { SubscriptionsService } from "./base";
import scheduler from "../utils/scheduler";
import DutyStatusLog, { ENGINE_STATUSES } from "../model/DutyStatusLog";
import Api from "./api";
import idSerializer from "geotab-uuid";
import { getServerDate } from "../utils/storage/date";
import Sync from "../sync/sync";
import { getCurrentVehicle } from "../utils/device";
import * as logger from "../utils/logger";
import { shouldAddExtraLogging, isVerboseLogging } from "../utils/logger.internal";
import { DriveStore } from "../store/reducers";
import { toDateTime } from "../utils/date";
import constants from "../constants";
import toastr from "../utils/toastr";
import { Device } from "../model/Device";
import { DataActions } from "../store/data/actions";
import { TApiSuccessCallback, TApiErrorCallback } from "../utils/api.interfaces";
import HosProcess from "../services/hosProcess";
import {DutyStatusLogService} from "../model/DutyStatusLogs";
import { NetworkStates } from "../store/mobile/types";
import { DeviceMovementActions } from "../store/deviceMovement/actions";
import { type THosOrigin, type THosStatus } from "hos-tools";

const FIVE_MPH = 8.04672;
const ZERO_SPEED = 0;
const SPEED_STATUS = 4;
const IGNITION_STATUS = 1;
const DATETIME_STATUS = 3;
const BAD_SERIAL = "badSerial";
const ON_LOG_CREATION_TASK = "@addIoxONLog";
const STATUS_D: THosStatus = "D";
const STATUS_INT_D: THosStatus = "INT_D";
const STATUS_PC: THosStatus = "PC";
const STATUS_PC_EXEMPTED: THosStatus = "PC_Exempted";
const STATUS_INT_PC: THosStatus = "INT_PC";
const STATUS_ON: THosStatus = "ON";
const STATUS_YM: THosStatus = "YM";
const STATUS_ENGINEPOWERUP: THosStatus = "EnginePowerup";
const STATUS_ENGINEPOWERUPPC: THosStatus = "EnginePowerupPC";
const STATUS_ENGINESHUTDOWN: THosStatus = "EngineShutdown";
const STATUS_ENGINESHUTDOWNPC: THosStatus = "EngineShutdownPC";
const SPEED_FROM_ENGINE = "Speed From Engine";
const ENGINE_ON = "IGN on";
const DATETIME_VALID = "Date/Time Valid";
const MOVING_STATUSES: THosStatus[] = [STATUS_D, STATUS_INT_D, STATUS_PC, STATUS_PC_EXEMPTED, STATUS_INT_PC, STATUS_YM];
const STATUSES_BEFORE_INTERMEDIATE_LOGS: THosStatus[] = [STATUS_D, STATUS_INT_D, STATUS_PC, STATUS_PC_EXEMPTED, STATUS_INT_PC];
const DRIVING_STATUSES: THosStatus[] = [STATUS_D, STATUS_INT_D];
const ENGINEPOWERUP_STATUSES: THosStatus[] = [STATUS_ENGINEPOWERUP, STATUS_ENGINEPOWERUPPC];
const ENGINESHUTDOWN_STATUSES: THosStatus[] = [STATUS_ENGINESHUTDOWN, STATUS_ENGINESHUTDOWNPC];

export const LOCK_SCREEN_TASK = "@lockScreen";
export interface IRawIoxData {
    latitude: number;
    longitude: number;
    driverId: number;
    engineHours: number;
    odometer: number | null;
    rpm: number;
    roadSpeed: number;
    tripDuration: number;
    tripOdometer: number;
    vehicleId: number;
    dateTime: string;
    status: string;
    rawData: string | number[];
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface IIoxLog extends IIdEntity {
    ioxStatus: string[];
    dateTime: Date;
    device: IIdEntity;
    driver: IIdEntity;
    location: IAddressLookupData;
    coDrivers: IIdEntity[];
    origin: THosOrigin;
    odometer: number;
    engineHours: number;
    _isTempIoxLog: boolean;
    roadSpeed: number;
}
/* eslint-enable @typescript-eslint/naming-convention */

let isOnLogCreationTaskStarted = false;

export function getDeviceBySerialNumber(api: Api, serialNumber: string, callbackSuccess?: TApiSuccessCallback, callbackError?: TApiErrorCallback) {
    api.call(
        "Get",
        {
            typeName: "Device",
            search: {
                SerialNumber: serialNumber,
                groups: [{id: "GroupVehicleId" }]
            }
        },
        result => {
            callbackSuccess && callbackSuccess(result);
        },
        err => {
            logger.error(logger.Source.USER, err.message, undefined, err);
            callbackError && callbackError(err);
        }
    );
}

export default class IoxDataHandler extends SubscriptionsService {
    private maxPCDistance: number;

    constructor(
        private readonly api: Api,
        private readonly sync: Sync,
        private readonly userService: UserService,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly store: DriveStore,
        readonly hosProcess: HosProcess
    ) {
        super();
        let systemSettings = store.getState().systemSettings;
        this.maxPCDistance = systemSettings.maxPCDistance ? systemSettings.maxPCDistance : -1;
    }

    /**
     * Handle and process incoming IOX data from NativeSDK or Cordova plugin
     * @param ioxData The IOX data itself, to be processed
     * @param isCordova Whether or not the source of the IOX data is from the Cordova plugin. RawData is totally broken
     * when coming out of Cordova, and there's nothing we can do to fix it. Vehicle Assignment is totally broken on Cordova
     * so just block it
     */
    checkNewIoxLog(ioxData: IRawIoxData, isCordova: boolean) {
        let serialNumber: string;
        let apiUser = this.api.getUser();
        let newLog = this.getNewLogFromIoxData(ioxData, apiUser);

        if (shouldAddExtraLogging(logger.Source.IOX) || isVerboseLogging()) {
            logger.event(logger.Event.IOXRAWDATA, JSON.stringify(ioxData), apiUser);
        }

        // See the GO device data spec https://developers.geotab.com/hardware/protocolReference/rs232UsbBle/#messages-from-external-device
        // and MYG-24072 for info on the raw data format
        serialNumber = isCordova ? BAD_SERIAL : String.fromCharCode(...(ioxData.rawData as number[]).slice(40, 52));

        this.checkSerialNumberAndHandleNewLog(serialNumber, apiUser, newLog);
    }

    /**
     * Checks for vehicle assignment using serial number, handles processing of new IOX log, and deals with the lock screen
     * @param newSerialNumber The serial number from the new IOX data
     * @param apiUser The api user
     * @param newLog The new log created from the IOX data
     */
    checkSerialNumberAndHandleNewLog(newSerialNumber: string, apiUser: IUser | null, newLog: IIoxLog) {
        let currentUser = this.userService.getCurrentUser();
        let activeDriver = this.userService.getDriversSeatUser();
        let isActiveDriver = apiUser && apiUser.name && activeDriver && sanitizeUserName(apiUser.name) === sanitizeUserName(activeDriver.name);
        let isSelectedAndAttachedIdMatch = newSerialNumber === BAD_SERIAL ? true : newSerialNumber === this.store.getState().data.currentHardwareId;

        if (!isSelectedAndAttachedIdMatch && currentUser) {
            this.store.dispatch(DataActions.setCurrentHardwareId(newSerialNumber));
            logger.log(logger.Source.IOX, `Connected to ${ newSerialNumber } via IOX.`);
            getDeviceBySerialNumber(
                this.api,
                newSerialNumber,
                result => {
                    if (result[0]) {
                        const deviceId = new Device(result[0]).id;
                        dispatcher.trigger(DispatcherEvents.HardwareIdCheck, false, deviceId);
                    } else {
                        this.store.dispatch(DataActions.setCurrentHardwareId(undefined));
                        toastr.error(
                            "Device not found in this database. Please contact your administrator.",
                            undefined,
                            undefined,
                            {
                                isUserNightMode: this.store.getState().data.nightMode
                            }
                        );
                    }
                },
                () => {
                    this.store.dispatch(DataActions.setCurrentHardwareId(undefined));
                    // Reset current hardware id if offline.
                    // But still trigger event using the serial number in case they are already assigned to the vehicle.
                    dispatcher.trigger(DispatcherEvents.HardwareIdCheck, false, undefined, newSerialNumber);
                    toastr.error("Device could not be attached due to network issues.", undefined, undefined, { isUserNightMode: this.store.getState().data.nightMode });
                }
            );
        } else if (isSelectedAndAttachedIdMatch && this.store.getState().mobile.online !== NetworkStates.ONLINE && isActiveDriver && newLog.ioxStatus[DATETIME_STATUS] === DATETIME_VALID) {
            if (newLog.ioxStatus[SPEED_STATUS] === SPEED_FROM_ENGINE) {
                this.processStandardStatuses(newLog);
            }
            this.processIgnitionStatuses(newLog);
            dispatcher.trigger(DispatcherEvents.HardwareIdCheck, true);
        } else if (isSelectedAndAttachedIdMatch && this.store.getState().mobile.online !== NetworkStates.ONLINE && isActiveDriver) {
            dispatcher.trigger(DispatcherEvents.HardwareIdCheck, true);
        } else {
            activeDriver && this.stopAddingNewLogTask(activeDriver.name);
            dispatcher.trigger(DispatcherEvents.HardwareIdCheck, true);
        }

        this.store.dispatch(DeviceMovementActions.setIoxDataPayload({
            newLog,
            activeDriverName: activeDriver?.name
        }));
    }

    calculateStatus(lastLog: DutyStatusLog | null, hosLogs?: DutyStatusLog[], newIoxLog?: IIoxLog) {
        if (!lastLog || (lastLog && MOVING_STATUSES.indexOf(lastLog.status) === -1)) {
            return STATUS_D;
        }
        if (lastLog && lastLog.isPCLog() && hosLogs && newIoxLog) {
            // Get the original PC log if the current log is an INT_PC
            let pcLog = this.dutyStatusLogService.getLatestPcLog(hosLogs);
            if (lastLog.status === "PC" || lastLog.status === "PC_Exempted") {
                pcLog = lastLog;
            }
            if (pcLog) {
                let user = this.userService.getCurrentUser() as IUser;
                let ruleSetKey: string = this.hosProcess.getValidRuleSet(this.hosProcess.getLatestHosRuleSetIdShortened(true));
                if (lastLog.status !== "PC_Exempted") {
                    let pcLimitDetails = this.dutyStatusLogService.isPCLimitExceeded(ruleSetKey, user, hosLogs, this.maxPCDistance, this.hosProcess, newIoxLog.odometer, this.sync);
                    if (pcLimitDetails.isPCLimitExceeded) {
                        return STATUS_D;
                    }
                }
            }
        }
        return null;
    }

    calculateIntermediateLogStatus(lastLog: DutyStatusLog | null) {
        let lastStatus = lastLog && lastLog.status;
        if (!lastStatus || STATUSES_BEFORE_INTERMEDIATE_LOGS.indexOf(lastStatus) > -1) {
            if (this.isHourLongStatus(lastLog)) {
               return (lastStatus || "").match(STATUS_D) ? STATUS_INT_D : STATUS_INT_PC;
            }
        }
        return null;
    }

    isHourLongStatus(lastLog: DutyStatusLog | null) {
        let lastLogTimeVal = lastLog ? lastLog.dateTime : new Date();
        let nowTime = getServerDate().getTime();
        let lastLogTime = toDateTime(lastLogTimeVal).getTime();

        if (nowTime - lastLogTime >= 3.6e+6) {
            return true;
        }
        return false;
    }

    addNewIoxLog(newLog: IIoxLog, status: THosStatus, logDateTime?: Date) {
        let activeDriver = this.userService.getDriversSeatUser();
        let currentDevice = getCurrentVehicle();
        let hosLog = new DutyStatusLog({
                id: idSerializer.get(),
                status,
                dateTime: logDateTime ? logDateTime.toISOString() : newLog.dateTime.toISOString(),
                device: currentDevice ? currentDevice : newLog.device,
                driver: newLog.driver,
                coDrivers: newLog.coDrivers,
                location: newLog.location,
                origin: newLog.origin,
                malfunction: "None",
                parentId: "",
                odometer: newLog.odometer, // Convert to meters
                engineHours: newLog.engineHours,
                _isTempIoxLog: newLog._isTempIoxLog
            });

        if (shouldAddExtraLogging(logger.Source.IOX)) {
            logger.event(logger.Event.IOXGENERATEDDSL, JSON.stringify(hosLog), newLog.driver.id);
        }

        activeDriver && ENGINE_STATUSES.indexOf(status) === -1 && this.stopAddingNewLogTask(activeDriver.name);
        void this.sync.dutyStatusLogs.addItem(hosLog).then(() => {
            (status === "OFF" || status === "ON") && dispatcher.trigger(DispatcherEvents.VehicleEndedStatus, hosLog);
        });
    }

    getNewLogFromIoxData(ioxData: IRawIoxData, apiUser: IUser | null) {
        let long = ioxData.longitude;
        let lat = ioxData.latitude;
        let statusesArray = ioxData.status.split(" | ", 6);
        dispatcher.trigger(
            DispatcherEvents.NewPositionDataForLockScreen,
            { latitude: ioxData.latitude, longitude: ioxData.longitude, dateTime: ioxData.dateTime, speed: ioxData.roadSpeed, source: "Iox" }
        );
        let ioxDateTime = ioxData.dateTime ?
                    Number.isNaN(new Date(ioxData.dateTime).getTime()) ?  new Date(parseInt(ioxData.dateTime)) : new Date(ioxData.dateTime)
                    : getServerDate();

        return {
            ioxStatus: statusesArray,
            dateTime: ioxDateTime,
            device: {id: "NoDeviceId"},
            driver: {id: apiUser ? apiUser.id : "NoUserId"},
            location: { location: { x: long, y: lat }},
            coDrivers: this.userService.getCoDrivers(apiUser ? apiUser.name : undefined),
            origin: "Automatic",
            odometer: ioxData.odometer ? ioxData.odometer * 100 : null,
            engineHours: ioxData.engineHours,
            _isTempIoxLog: true,
            roadSpeed: ioxData.roadSpeed
        } as IIoxLog;
    }

    startSubscriptions() {
        this.stopSubscriptions();
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.on(DispatcherEvents.NewIoxData, this.checkNewIoxLog, this);
    }

    stopSubscriptions() {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.off(DispatcherEvents.NewIoxData, this.checkNewIoxLog, this);
    }

    setMaxPCDistance(distance: number) {
        this.maxPCDistance = distance;
    }

    getMaxPCDistance() {
        return this.maxPCDistance;
    }

    private processStandardStatuses(log: IIoxLog) {
        let newLog = log;
        let hosLogs = this.dutyStatusLogService.getHOSLogs();
        let lastLog = this.dutyStatusLogService.getLatestLog(hosLogs);
        let intermediateLogStatus = this.calculateIntermediateLogStatus(lastLog);
        let status: THosStatus | null = STATUS_ON;
        let activeDriver = this.userService.getDriversSeatUser();
        let apiUser = this.api.getUser();
        newLog.driver = { id: apiUser && apiUser.id ? apiUser.id : "NoUserId" };

        if (newLog.roadSpeed >= FIVE_MPH && intermediateLogStatus) {
            this.addNewIoxLog(newLog, intermediateLogStatus);
        } else if (newLog.roadSpeed >= FIVE_MPH) {
            status = this.calculateStatus(lastLog, hosLogs, newLog);
            if (status) {
                this.addNewIoxLog(newLog, status);
            }
            activeDriver && this.stopAddingNewLogTask(activeDriver.name);
        } else if (newLog.roadSpeed === ZERO_SPEED && lastLog && DRIVING_STATUSES.indexOf(lastLog.status) > -1) {
            activeDriver && this.scheduleOnLogCreation(activeDriver.name, newLog, status);
        } else {
            activeDriver && this.stopAddingNewLogTask(activeDriver.name);
        }

        return newLog;
    }

    private processIgnitionStatuses(log: IIoxLog) {
        let newLog = log;
        let hosLogs = this.dutyStatusLogService.getHOSLogs();
        let systemHosLogs = this.dutyStatusLogService.getHOSLogs(true, undefined, false, true);
        let lastIgnitionLog = this.dutyStatusLogService.getLatestEngineLog(systemHosLogs);
        let lastStandardLog = this.dutyStatusLogService.getLatestLog(hosLogs, undefined);
        let apiUser = this.api.getUser();
        newLog.driver = { id: apiUser && apiUser.id ? apiUser.id : "NoUserId" };
        let status: THosStatus;

        if (lastStandardLog && lastStandardLog.isPCLog()) {
            status = log.ioxStatus[IGNITION_STATUS] === ENGINE_ON ? "EnginePowerupPC" : "EngineShutdownPC";
        } else {
            status = log.ioxStatus[IGNITION_STATUS] === ENGINE_ON ? "EnginePowerup" : "EngineShutdown";
        }

        if (!lastIgnitionLog || this.isEngineLogValid(lastIgnitionLog.status, status)) {
            this.addNewIoxLog(newLog, status);
            if (ENGINEPOWERUP_STATUSES.indexOf(status) > -1) {
                this.checkIfNeedToCreateEngineCycleLog(newLog, lastIgnitionLog, lastStandardLog);
            }
        }

        return newLog;
    }

    private isEngineLogValid(lastIgnitionStatus: THosStatus, currentStatus: THosStatus): boolean {
        if (ENGINEPOWERUP_STATUSES.indexOf(lastIgnitionStatus) !== -1 && ENGINEPOWERUP_STATUSES.indexOf(currentStatus) !== -1) {
            return false;
        } else if (ENGINESHUTDOWN_STATUSES.indexOf(lastIgnitionStatus) !== -1 && ENGINESHUTDOWN_STATUSES.indexOf(currentStatus) !== -1) {
            return false;
        }
        return true;
    }

    private checkIfNeedToCreateEngineCycleLog(newLog: IIoxLog, lastIgnitionLog: DutyStatusLog | null, lastStandardLog: DutyStatusLog | null) {
        if (!lastIgnitionLog || !lastStandardLog) {
            return;
        }
        if (!lastIgnitionLog.isEngineShutDownLog()) {
            return;
        }
        if (lastStandardLog.status !== "YM" && lastStandardLog.status !== "PC") {
            return;
        }
        if (lastStandardLog.dateTime > lastIgnitionLog.dateTime) {
            return;
        }
        let status: THosStatus = lastStandardLog.status === "YM" ? "ON" : "OFF";
        this.addNewIoxLog(newLog, status, new Date(new Date(newLog.dateTime).getTime() + 10));
    }

    private stopAddingNewLogTask(activeDriverName: string) {
        if (isOnLogCreationTaskStarted) {
            scheduler.stop(ON_LOG_CREATION_TASK, activeDriverName);
            isOnLogCreationTaskStarted = false;
        }
    }

    private scheduleOnLogCreation(activeDriverName: string, newLog: IIoxLog, status: THosStatus) {
        if (!scheduler.isTaskRegistered(ON_LOG_CREATION_TASK, activeDriverName) || !isOnLogCreationTaskStarted) {
            if (!isOnLogCreationTaskStarted) {
                scheduler.unregisterTask(ON_LOG_CREATION_TASK, activeDriverName);
            }

            scheduler.registerTask(ON_LOG_CREATION_TASK, () => this.addNewIoxLog(newLog, status), constants.HOS.CHECK_ON_LOG_INTERVAL, activeDriverName, true)
                .start(ON_LOG_CREATION_TASK, activeDriverName);
            isOnLogCreationTaskStarted = true;
        }
    }
}

export function isIoxUsbDeviceAttached(store: DriveStore): boolean {
    return !!store.getState().mobile.ioxAttached && !!store.getState().data.currentHardwareId;
}